import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip, Stack, Paper, Button } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TodayIcon from '@mui/icons-material/Today';

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function HolidayCalendarGrid({ records, onAdd, onEdit }) {
  const [currentDate, setCurrentDate] = useState(new Date());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const getFirstDayOfMonth = (y, m) => new Date(y, m, 1).getDay();

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToToday = () => setCurrentDate(new Date());

  const todayStr = new Date().toISOString().split('T')[0];

  const getDayHolidays = (day) => {
    const dStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const mStr = `-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return records.filter(r => {
      if (!r.holiday_date) return false;
      if (r.recurring_flag === 'Y') {
        return r.holiday_date.endsWith(mStr);
      } else {
        return r.holiday_date === dStr;
      }
    });
  };

  const handleDayClick = (day) => {
    const dStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    onAdd({ holiday_date: dStr });
  };

  const getHolidayColor = (h, dStr) => {
    if (h.active_flag === 'N' || h.active_flag === 'Inactive') return { bg: '#F1F5F9', color: '#94A3B8', border: '#E2E8F0' }; // Inactive (Muted)
    const effectiveDateStr = h.recurring_flag === 'Y' ? dStr : h.holiday_date;
    if (effectiveDateStr < todayStr) return { bg: '#F3F4F6', color: '#6B7280', border: '#D1D5DB' }; // Past (Gray)
    if (effectiveDateStr === todayStr) return { bg: '#EFF6FF', color: '#2563EB', border: '#BFDBFE' }; // Today (Blue)
    return { bg: '#F0FDF4', color: '#16A34A', border: '#BBF7D0' }; // Upcoming (Green)
  };

  const renderCells = () => {
    const cells = [];
    // Empty cells before the first day
    for (let i = 0; i < firstDay; i++) {
      cells.push(<Box key={`empty-${i}`} sx={{ borderRight: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB', bgcolor: '#F9FAFB' }} />);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isToday = dStr === todayStr;
      const holidays = getDayHolidays(day);

      cells.push(
        <Box
          key={day}
          onClick={() => handleDayClick(day)}
          sx={{
            borderRight: '1px solid #E5E7EB',
            borderBottom: '1px solid #E5E7EB',
            minHeight: 120,
            p: 1,
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            '&:hover': { bgcolor: '#F3F4F6' },
            bgcolor: isToday ? '#FFFBEB' : 'white',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: isToday ? 700 : 500,
              color: isToday ? '#D97706' : '#374151',
              mb: 1,
              width: 28, height: 28,
              borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              ...(isToday && { bgcolor: '#FEF3C7' })
            }}
          >
            {day}
          </Typography>
          <Stack spacing={0.5}>
            {holidays.map(h => {
              const colors = getHolidayColor(h, dStr);
              return (
                <Tooltip key={h.id || h.holiday_id} title={`${h.holiday_name} (${h.holiday_type}) - ${h.recurring_flag === 'Y' ? 'Recurring' : 'One-Time'}`} arrow>
                  <Box
                    onClick={(e) => { e.stopPropagation(); onEdit(h); }}
                    sx={{
                      fontSize: '0.75rem',
                      px: 1, py: 0.5,
                      borderRadius: 1,
                      bgcolor: colors.bg,
                      color: colors.color,
                      border: `1px solid ${colors.border}`,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontWeight: 500,
                      '&:hover': { filter: 'brightness(0.95)' }
                    }}
                  >
                    {h.holiday_name}
                  </Box>
                </Tooltip>
              );
            })}
          </Stack>
        </Box>
      );
    }

    // Remaining empty cells
    const remaining = 42 - cells.length; // 6 rows * 7 days
    for (let i = 0; i < remaining; i++) {
      cells.push(<Box key={`empty-end-${i}`} sx={{ borderRight: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB', bgcolor: '#F9FAFB' }} />);
    }

    return cells;
  };

  return (
    <Paper sx={{ borderRadius: 2, border: '1px solid #E5E7EB', overflow: 'hidden', boxShadow: 'none' }}>
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #E5E7EB', bgcolor: 'white' }}>
        <Typography variant="h6" fontWeight={600} color="#111827">
          {currentDate.toLocaleString('default', { month: 'long' })} {year}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="outlined" size="small" onClick={goToToday} startIcon={<TodayIcon />} sx={{ textTransform: 'none', borderColor: '#D1D5DB', color: '#374151', '&:hover':{bgcolor:'#F9FAFB', borderColor:'#D1D5DB'} }}>
            Today
          </Button>
          <IconButton onClick={prevMonth} size="small" sx={{ border: '1px solid #E5E7EB', borderRadius: 1 }}>
            <NavigateBeforeIcon />
          </IconButton>
          <IconButton onClick={nextMonth} size="small" sx={{ border: '1px solid #E5E7EB', borderRadius: 1 }}>
            <NavigateNextIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Days Header */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
        {DAYS_OF_WEEK.map((day, idx) => (
          <Box key={day} sx={{ p: 1.5, textAlign: 'center', borderRight: idx < 6 ? '1px solid #E5E7EB' : 'none' }}>
            <Typography variant="caption" fontWeight={600} color="#6B7280" textTransform="uppercase">
              {day}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {renderCells()}
      </Box>
    </Paper>
  );
}
